/*
 * ------------------------------------------------------------
 * <sticky-buy-bar> — reusable PDP sticky add-to-cart bar
 *
 * Mirrors a page's existing "Add to cart" button (label,
 * disabled state and an optional `data-sticky-meta` summary)
 * and forwards its own clicks to that button, so it reuses
 * whatever cart logic the host PDP already has. It reveals
 * itself once the observed element scrolls out of the viewport.
 *
 * Contract (set by snippets/sticky-buy-bar.liquid):
 *   data-source   — required CSS selector for the real ATC button
 *   data-observe  — optional selector to watch for visibility
 *                   (defaults to the source button)
 *
 * The host PDP keeps `data-sticky-meta` on the source button
 * up to date with the current selection summary.
 * ------------------------------------------------------------
 */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const sourceSel = this.dataset.source;
    const observeSel = this.dataset.observe || sourceSel;

    this.source = sourceSel ? document.querySelector(sourceSel) : null;
    this.observeTarget = observeSel ? document.querySelector(observeSel) : this.source;
    this.button = this.querySelector('[data-sticky-buy-bar-atc]');
    this.metaEl = this.querySelector('[data-sticky-buy-bar-meta]');

    if (!this.source || !this.button) return;

    this.sync();

    // Keep the bar in sync when the source button changes.
    this._mutationObserver = new MutationObserver(() => this.sync());
    this._mutationObserver.observe(this.source, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
      attributeFilter: ['disabled', 'data-variant-id', 'data-sticky-meta'],
    });

    // Forward clicks to the real button so cart logic lives in one place.
    this.button.addEventListener('click', () => {
      if (!this.button.disabled) this.source.click();
    });

    // Don't auto-reveal inside the theme editor.
    if (window.Shopify && window.Shopify.designMode) return;

    if (this.observeTarget && 'IntersectionObserver' in window) {
      this._intersectionObserver = new IntersectionObserver(
        (entries) => entries.forEach((entry) => this.toggle(!entry.isIntersecting)),
        { rootMargin: '0px 0px -40px 0px' }
      );
      this._intersectionObserver.observe(this.observeTarget);
    } else {
      const target = this.observeTarget || this.source;
      this._onScroll = () => {
        const rect = target.getBoundingClientRect();
        const visible = rect.top < window.innerHeight && rect.bottom > 0;
        this.toggle(!visible);
      };
      const handler =
        window.theme && theme.utils && theme.utils.throttle
          ? theme.utils.throttle(this._onScroll)
          : this._onScroll;
      this._scrollHandler = handler;
      window.addEventListener('scroll', handler, { passive: true });
      this._onScroll();
    }
  }

  sync() {
    if (!this.source || !this.button) return;

    const textEl = this.source.querySelector('.btn-text');
    const label = (textEl ? textEl.textContent : this.source.textContent).trim();
    if (label) this.button.textContent = label;

    this.button.disabled = this.source.disabled;

    if (this.metaEl) {
      this.metaEl.textContent = this.source.dataset.stickyMeta || '';
    }
  }

  toggle(show) {
    if (show) {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      // Force reflow so the slide-in transition runs after unhiding.
      void this.offsetHeight;
      this.classList.add('is-visible');
    } else {
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      this.hidden = true;
    }
  }

  disconnectedCallback() {
    if (this._mutationObserver) this._mutationObserver.disconnect();
    if (this._intersectionObserver) this._intersectionObserver.disconnect();
    if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
